import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getUserFullPermissions } from "../services/permissionService";
import { getUserId, getTeamID, JSONParser } from "../common/function";
import { authHeader } from "../common/mainfunction";
import { WORKFLOW_USER_ASSIGNMENTS_URL } from "../common/url";
import API from "../constants/API";
import type { Permission as ApiPermission } from "../types/api";

type Permission = ApiPermission;

interface QueueAssignment {
  queueName: string;
  [key: string]: unknown;
}

interface PermissionContextValue {
  // State
  permissions: Permission[];
  permissionKeys: string[];
  loading: boolean;
  error: string | null;

  // Queue assignments
  queueAssignments: QueueAssignment[];
  queueNames: string[];

  // Permission checks
  hasPermission: (permissionKey: string) => boolean;
  hasAnyPermission: (permissionKeyList: string[]) => boolean;
  hasAllPermissions: (permissionKeyList: string[]) => boolean;

  // Queue checks
  isAssignedToQueue: (queueName: string) => boolean;
  isAssignedToAnyQueue: (queueNameList: string[]) => boolean;

  // Actions
  refreshPermissions: () => Promise<void>;
  clearPermissions: () => void;
}

const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

interface PermissionProviderProps {
  children: React.ReactNode;
}

/**
 * Permission Provider
 * Provides permission state and helper functions to the entire app
 */
export const PermissionProvider = ({ children }: PermissionProviderProps) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permissionKeys, setPermissionKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Queue assignment state
  const [queueAssignments, setQueueAssignments] = useState<QueueAssignment[]>([]);
  const [queueNames, setQueueNames] = useState<string[]>([]);

  /**
   * Load user's permissions from API with retry logic
   */
  const loadUserPermissions = useCallback(async (retryCount: number = 0): Promise<void> => {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000; // 1 second

    try {
      setLoading(true);
      setError(null);
      const userId = getUserId();

      if (!userId) {
        // User not logged in, clear permissions
        setPermissions([]);
        setPermissionKeys([]);
        setLoading(false);
        return;
      }

      const response = await getUserFullPermissions(userId);

      if (response.status === "Success" && response.data) {
        setPermissions(response.data.permissions || []);
        setPermissionKeys(response.data.permissionKeys || []);
      } else {
        setError("Failed to load permissions");
      }
    } catch (err: unknown) {
      console.error("[PermissionContext] Error loading permissions:", err);

      // Retry on failure (handles transient connection errors)
      if (retryCount < MAX_RETRIES) {
        console.log(
          `[PermissionContext] Retrying in ${RETRY_DELAY}ms... (attempt ${retryCount + 1}/${MAX_RETRIES})`
        );
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        return loadUserPermissions(retryCount + 1);
      }

      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load queue assignments from workflow API
   */
  const loadQueueAssignments = useCallback(async (): Promise<void> => {
    try {
      const userId = getUserId();
      const teamId = getTeamID();
      if (!userId || !teamId) return;

      const config = {
        method: "GET",
        url: WORKFLOW_USER_ASSIGNMENTS_URL,
        headers: authHeader(),
        params: { userId: parseInt(userId, 10), teamId: parseInt(teamId, 10) },
      };

      const response = await API(config);
      const decrypted = response.data;
      const parsed = JSONParser(decrypted, { status: null, data: [] });

      if (parsed.status === "Success" && parsed.data) {
        setQueueAssignments(parsed.data);
        setQueueNames(parsed.data.map((q: QueueAssignment) => q.queueName));
      }
    } catch (err) {
      console.error("[PermissionContext] Error loading queue assignments:", err);
    }
  }, []);

  /**
   * Load permissions on mount and when userId changes
   */
  useEffect(() => {
    const userId = getUserId();
    if (userId) {
      loadUserPermissions();
      loadQueueAssignments();
    } else {
      setLoading(false);
    }
  }, [loadUserPermissions, loadQueueAssignments]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permissionKey: string): boolean => {
      return permissionKeys.includes(permissionKey);
    },
    [permissionKeys]
  );

  /**
   * Check if user has ANY of the specified permissions
   */
  const hasAnyPermission = useCallback(
    (permissionKeyList: string[]): boolean => {
      if (!Array.isArray(permissionKeyList)) return false;
      return permissionKeyList.some((key) => permissionKeys.includes(key));
    },
    [permissionKeys]
  );

  /**
   * Check if user has ALL of the specified permissions
   */
  const hasAllPermissions = useCallback(
    (permissionKeyList: string[]): boolean => {
      if (!Array.isArray(permissionKeyList)) return false;
      return permissionKeyList.every((key) => permissionKeys.includes(key));
    },
    [permissionKeys]
  );

  /**
   * Check if user is assigned to a specific queue
   */
  const isAssignedToQueue = useCallback(
    (queueName: string): boolean => queueNames.includes(queueName),
    [queueNames]
  );

  /**
   * Check if user is assigned to any of the specified queues
   */
  const isAssignedToAnyQueue = useCallback(
    (queueNameList: string[]): boolean => {
      if (!Array.isArray(queueNameList)) return false;
      return queueNameList.some((name) => queueNames.includes(name));
    },
    [queueNames]
  );

  /**
   * Refresh permissions from API
   */
  const refreshPermissions = useCallback(async (): Promise<void> => {
    loadQueueAssignments();
    return loadUserPermissions();
  }, [loadUserPermissions, loadQueueAssignments]);

  /**
   * Clear permissions (e.g., on logout)
   */
  const clearPermissions = useCallback((): void => {
    setPermissions([]);
    setPermissionKeys([]);
    setQueueAssignments([]);
    setQueueNames([]);
  }, []);

  const value: PermissionContextValue = {
    // State
    permissions,
    permissionKeys,
    loading,
    error,

    // Queue assignments
    queueAssignments,
    queueNames,

    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Queue checks
    isAssignedToQueue,
    isAssignedToAnyQueue,

    // Actions
    refreshPermissions,
    clearPermissions,
  };

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

/**
 * Hook to access permission context
 */
export const usePermissions = (): PermissionContextValue => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
};

/**
 * Permission constants for easy reference (21 permissions)
 */
export const PERMISSIONS = {
  // Invoice (6)
  INVOICE_VIEW_ASSIGNED: "invoice.view.assigned",
  INVOICE_VIEW_ALL: "invoice.view.all",
  INVOICE_UPLOAD: "invoice.upload",
  INVOICE_EDIT: "invoice.edit",
  INVOICE_APPROVE: "invoice.approve",
  INVOICE_LOCK: "invoice.lock",
  INVOICE_DELETE: "invoice.delete",

  // Exception (5)
  EXCEPTION_REQUEST_INFO: "exception.handle.request_info",
  EXCEPTION_SUPPLIER: "exception.handle.supplier",
  EXCEPTION_SPECIALIST: "exception.handle.specialist",
  EXCEPTION_REJECTION: "exception.handle.rejection",
  EXCEPTION_DUPLICATE: "exception.handle.duplicate",

  // Admin (10)
  ADMIN_USERS_VIEW: "admin.users.view",
  ADMIN_USERS_MANAGE: "admin.users.manage",
  ADMIN_USERS_PERMISSIONS: "admin.users.permissions",
  ADMIN_SETTINGS_VIEW: "admin.settings.view",
  ADMIN_SETTINGS_MANAGE: "admin.settings.manage",
  ADMIN_SUPPLIERS_VIEW: "admin.suppliers.view",
  ADMIN_SUPPLIERS_MANAGE: "admin.suppliers.manage",
  ADMIN_REPORTS_VIEW: "admin.reports.view",
  ADMIN_GLCODES_MANAGE: "admin.glcodes.manage",
  ADMIN_AUDIT_VIEW: "admin.audit.view",
} as const;

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export default PermissionContext;
